import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { Table, Input, Icon, Button, Checkbox, Radio, DatePicker, notification, Tooltip } from 'antd';
import { TableProps, ColumnProps, CompareFn } from 'antd/lib/table';
import { PaginationProps } from 'antd/lib/pagination';
import { RangePickerValue } from 'antd/lib/date-picker/interface';
import moment from 'moment';
import { Moment } from 'moment';
// import * as moment from 'moment';
import { downloadFile } from 'src/utils/DownloadUtil';

import TableColumnState from 'src/models/datatable/TableColumnState';
import TableRequestFilterDTO from 'src/models/datatable/TableRequestFilterDTO';
import TableRequestDTO from 'src/models/datatable/TableRequestDTO';
import TableResponseDTO from 'src/models/datatable/TableResponseDTO';

const ButtonGroup = Button.Group;
const { RangePicker } = DatePicker;
const RadioGroup = Radio.Group;

import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';

export enum Renderer {
    DEFAULT,
    BooleanYesNo,
    BooleanYesEmpty,
    BooleanCheckbox,
    ShortDate,
    ShortDateTime,
    Currency,
    Number,
    NumberAllowZero,
    LinkButtons
}

export enum Filterer {
    NONE,
    Text,
    Date,
    DateRange,
    Dropdown,
    BooleanCheckbox,
    BooleanRadio,
    RadioSelect
}

export interface DataTableColumnProps<T> {
    renderer?: Renderer;
    filterType?: Filterer;
    renderDataTransform?: (value: any, record: T) => any;
    columnProps: ColumnProps<T>;
    toolTipText?: JSX.Element;
    icon?: string;
    /** Used with the RadioSelect Filter */
    filterItems?: string[];
}

export interface DataTableProps<T> {
    className?: string;
    serverSide?: boolean;
    data: T[];
    fetchData?: (requestState: TableRequestDTO, checkEcho: () => boolean, callback: (response: TableResponseDTO<T>) => void) => any;
    columns: DataTableColumnProps<T>[];
    buttonBar?: DataTableButton[];
    tableProps: TableProps<T>;
}

enum DataTableButtonType {
    Custom,
    Export,
    Reset,
    Link
}

export class DataTableButton {
    protected type: DataTableButtonType = DataTableButtonType.Custom;
    public label: React.ReactNode;
    public id: string;
    public onClick: () => void;

    constructor(id: string, onClick: () => void) {
        this.id = id;
        this.label = <Translate id={id} />;
        this.onClick = onClick;
    }

    getType() {
        return this.type;
    }
}

export class DataTableButtonExport extends DataTableButton {
    public filename: string;
    // public addDateToFilename: boolean;
    public onComplete: () => void;

    constructor(filename: string) {
        super('dataTable.export', () => console.log('export clicked'));
        this.type = DataTableButtonType.Export;
        this.filename = filename;
        // TODO: JB - Removed because I am out of time
        // this.addDateToFilename = addDateToFilename != null ? addDateToFilename : false;
        // this.onComplete = onComplete;
    }
}

export class DataTableButtonLink extends DataTableButton {
    public href: string;

    constructor(id: string, href: string, onClick?: () => void) {
        super(id, onClick != null ? onClick : () => { });
        this.type = DataTableButtonType.Link;
        this.href = href;
    }
}

export class DataTableButtonReset extends DataTableButton {
    constructor() {
        super("dataTable.reset", () => console.log('reset clicked'));
        this.type = DataTableButtonType.Reset;
    }
}

export interface DataTablePaginationProps extends PaginationProps {
    dataTableUnfilteredTotal?: number | undefined;
}

interface ColumnOptionalOptions<T> {
    width?: number;
    /** Indicates that this column should be fixed to one side */
    fixed?: "left" | "right";
    align?: "left" | "right" | "center";
    /** The render type for this column */
    renderer?: keyof typeof Renderer;
    /** Affects how the filter is rendered */
    filterType?: keyof typeof Filterer;
    /** The filter items used **exclusively** for the RadioSelect Filter */
    filterItems?: string[];
    /** You will want either a `true`/`false` or a `(a, b) => a.order - b.order` or similar */
    sorter?: boolean | CompareFn<T>;

    /** The render function. Overriding this ignores the `renderer` property, naturally, since you are doing it yourself */
    render?: (record: T, index?: number) => React.ReactNode;
}

interface DataTableState<T> {
    pagination: DataTablePaginationProps;
    sorter: any;
    filters: TableRequestFilterDTO[];
    columnStates: TableColumnState[];
    data: T[];
    filteredData: T[] | null;
}

class DataTable<T> extends React.Component<DataTableProps<T>, DataTableState<T>> {
    public static DefaultPagination: DataTablePaginationProps = {
        defaultPageSize: 10,
        defaultCurrent: 1
    };

    public static StandardTotalsRenderer = (total: number, range: [number, number]): React.ReactNode => (`Showing ${range[0]} to ${range[1]} of ${total} total`);

    public static StandardColumns: any = {
        Number<T>(translationId: string, dataIndex: string): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: <Translate id={translationId} />,
                    dataIndex: dataIndex,
                    sorter: true,
                },
                filterType: Filterer.Text,
                renderer: Renderer.Number,
            };
        },
        NumberAllowZero<T>(translationId: string, dataIndex: string): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: <Translate id={translationId} />,
                    dataIndex: dataIndex,
                    sorter: true,
                },
                filterType: Filterer.Text,
                renderer: Renderer.NumberAllowZero,
            };
        },
        Text<T>(translationId: string, dataIndex: string): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: <Translate id={translationId} />,
                    dataIndex: dataIndex,
                    sorter: true,
                },
                filterType: Filterer.Text
            };
        },
        Boolean<T>(translationId: string, dataIndex: string): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: <Translate id={translationId} />,
                    dataIndex: dataIndex,
                    sorter: true,
                },
                renderer: Renderer.BooleanCheckbox,
                filterType: Filterer.BooleanCheckbox
            };
        },
        Date<T>(translationId: string, dataIndex: string): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: <Translate id={translationId} />,
                    dataIndex: dataIndex,
                    sorter: true
                },
                filterType: Filterer.Date,
                renderer: Renderer.ShortDate
            };
        },
        DateTime<T>(translationId: string, dataIndex: string): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: <Translate id={translationId} />,
                    dataIndex: dataIndex,
                    sorter: true
                },
                filterType: Filterer.Date,
                renderer: Renderer.ShortDateTime
            };
        },
        Link<T>(translationId: string, key: string, transform: (value: any, record: T) => any, icon: string): DataTableColumnProps<T> {
            return {
                columnProps: {
                    key: key,
                    align: 'center',
                    width: 35
                },
                renderer: Renderer.LinkButtons,
                renderDataTransform: (text, record) => transform(text, record),
                toolTipText: <Translate id={translationId} />,
                icon: icon
            };
        },
    };

    /** Acts like the *StandardColumns* but with proper intellisense! */
    public static AdvancedColumns = {
        Number<T>(title: React.ReactNode, dataIndex: keyof T, width: number = undefined): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: title,
                    dataIndex: dataIndex as string,
                    sorter: true,
                    width: width
                },
                filterType: Filterer.Text,
                renderer: Renderer.Number,
            };
        },
        Text<T>(title: React.ReactNode, dataIndex: keyof T, width: number = undefined): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: title,
                    dataIndex: dataIndex as string,
                    sorter: true,
                    width: width
                },
                filterType: Filterer.Text
            };
        },
        /** Text column but with better filtering */
        Enum<T>(title: React.ReactNode, dataIndex: keyof T, filterItems: string[], width: number = undefined, render: (text: any, record: T, index: number) => React.ReactNode = undefined): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: title,
                    dataIndex: dataIndex as string,
                    sorter: true,
                    width: width,
                    render: render
                },
                filterType: Filterer.RadioSelect,
                filterItems
            };
        },
        Boolean<T>(title: React.ReactNode, dataIndex: keyof T, width: number = undefined): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: title,
                    dataIndex: dataIndex as string,
                    sorter: true,
                    width: width
                },
                renderer: Renderer.BooleanCheckbox,
                filterType: Filterer.BooleanCheckbox
            };
        },
        DateTime<T>(title: React.ReactNode, dataIndex: keyof T, width: number = undefined): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: title,
                    dataIndex: dataIndex as string,
                    sorter: true,
                    width: width
                },
                renderer: Renderer.ShortDateTime,
                filterType: Filterer.Date
            };
        },
        Date<T>(title: React.ReactNode, dataIndex: keyof T, width: number = undefined): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: title,
                    dataIndex: dataIndex as string,
                    sorter: true,
                    width: width
                },
                renderer: Renderer.ShortDate,
                filterType: Filterer.Date
            };
        },
        /** Old, use normal Custom instead */
        CustomLegacy<T>(title: React.ReactNode, dataIndex: keyof T, width: number = undefined, renderer: keyof typeof Renderer = "DEFAULT", filterer: keyof typeof Filterer = "Text", render: (text: any, record: T, index: number) => React.ReactNode = undefined): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: title,
                    dataIndex: dataIndex as string,
                    sorter: true,
                    width: width,
                    render: render
                },
                renderer: Renderer[renderer],
                filterType: Filterer[filterer]
            };
        },
        Custom<T>(title: React.ReactNode, dataIndex: keyof T, width: number = undefined, render: (record: T, index: number) => React.ReactNode = undefined): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: title,
                    dataIndex: dataIndex as string,
                    sorter: true,
                    width: width,
                    render: (text: any, record: T, index: number) => render(record, index)
                },
                renderer: Renderer.DEFAULT,
                filterType: Filterer.Text
            };
        }
    };

    /** The 3rd form of the columns. Better custom support and better optional params */
    public static BetterColumns = {
        Custom<T>(title: React.ReactNode, dataIndex: keyof T, optional?: ColumnOptionalOptions<T>): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: title,
                    dataIndex: dataIndex as string,
                    sorter: optional?.sorter != null ? optional.sorter : true,
                    width: optional?.width,
                    render: optional?.render != null ? (_, record: T, index: number) => optional.render(record, index) : undefined,
                    fixed: optional?.fixed,
                    align: optional?.align
                },
                renderer: Renderer[optional?.renderer] || Renderer.DEFAULT,
                filterType: Filterer[optional?.filterType] || Filterer.Text,
                filterItems: optional?.filterItems || undefined
            };
        },
        /**Last resort if you absolutely need full control */
        FullManual<T>(title?: React.ReactNode, dataIndex?: string, optional?: ColumnOptionalOptions<T>): DataTableColumnProps<T> {
            return {
                columnProps: {
                    title: title,
                    dataIndex: dataIndex as string || "",
                    sorter: optional?.sorter != null ? optional.sorter : true,
                    width: optional?.width,
                    render: optional?.render != null ? (_, record: T, index: number) => optional.render(record, index) : undefined,
                    fixed: optional?.fixed,
                    align: optional?.align
                },
                renderer: Renderer[optional?.renderer] || Renderer.DEFAULT,
                filterType: Filterer[optional?.filterType] || Filterer.Text,
                filterItems: optional?.filterItems || undefined
            };
        }
    };

    private searchInputs: any = {};
    private echo = 0;

    constructor(props: DataTableProps<T>) {
        super(props);

        let $this = this;
        this.state = {
            // pagination: DataTable.DefaultPagination,
            pagination: {
                ...DataTable.DefaultPagination,
                ...(props.tableProps != null && props.tableProps.pagination != null ? props.tableProps.pagination : {}),
            },
            sorter: {},
            filters: [],
            columnStates: this.props.columns.map(column => {
                return TableColumnState.create({ columnProp: column.columnProps.dataIndex || column.columnProps.key });
            }),
            data: [],
            filteredData: null
        };

        // this.search = debounce(this.search, 400);
        this.handleTableChange = this.handleTableChange.bind(this);
    }

    componentDidMount() {
        if (this.props.serverSide) {
            this.callFetchData(this.state.pagination, this.state.filters, this.state.sorter);
        } else {
            this.setState({
                data: this.props.data
            });
        }
    }

    resetTable() {
        let pager = { ...this.state.pagination };
        pager.current = 1;

        let columnStates = this.state.columnStates.map(colState => {
            colState.filtered = false;
            colState.filterVisible = false;
            colState.searchText = '';
            return colState;
        });

        this.setState({
            pagination: pager,
            sorter: {},
            filters: [],
            columnStates
        });

        this.callFetchData(pager, [], {});
    }

    /** Note: Export must be enabled in the datatable provider */
    exportTable(filename: string) {
        if (!this.props.serverSide) {
            // Sorry, only server side for now
            return;
        }

        this.callExportData(this.state.pagination, this.state.filters, this.state.sorter, filename);
    }

    refresh() {
        if (this.props.serverSide) {
            this.callFetchData(this.state.pagination, this.state.filters, this.state.sorter);
        } else {
            this.setState(
                {
                    data: this.props.data,
                    // filteredData: this.props.data
                    filteredData: null,
                },
                () => {
                    this.state.columnStates.filter(colState => colState.filtered).forEach(colState => this.onSearch(colState));
                }
            );
        }
    }

    render() {
        var tableProps = { ...this.props.tableProps };
        tableProps.dataSource = this.state.filteredData || this.state.data;
        tableProps.pagination = this.state.pagination;
        tableProps.size = tableProps.size || 'small';
        tableProps.onChange = this.handleTableChange;
        tableProps.locale = { emptyText: <Translate id='dataTable.noData' /> };

        tableProps.columns = this.props.columns.map(column => {
            var columnProps = { ...column.columnProps };
            var columnState = this.getColumnState(column);

            // if (!column.columnProps.key) {
            //     column.columnProps.key = column.columnProps.dataIndex;
            // }

            switch (column.filterType) {
                case Filterer.Text:
                    columnProps.filterDropdown = this.textFilter(columnState, column.columnProps.title);
                    columnProps.filterDropdownVisible = columnState.filterVisible;
                    columnProps.onFilterDropdownVisibleChange = this.onFilterVisibleChangeWithFocus(columnState);
                    columnProps.filterIcon = this.filterIcon(columnState);
                    break;
                case Filterer.Date:
                    columnProps.filterDropdown = this.dateFilter(columnState, column.columnProps.title);
                    columnProps.filterDropdownVisible = columnState.filterVisible;
                    columnProps.onFilterDropdownVisibleChange = this.onFilterVisibleChangeWithFocus(columnState);
                    columnProps.filterIcon = this.filterIcon(columnState);
                    break;
                case Filterer.DateRange:
                    columnProps.filterDropdown = this.dateRangeFilter(columnState, column.columnProps.title);
                    columnProps.filterDropdownVisible = columnState.filterVisible;
                    columnProps.onFilterDropdownVisibleChange = this.onFilterVisibleChangeWithFocus(columnState);
                    columnProps.filterIcon = this.filterIcon(columnState);
                    break;
                case Filterer.BooleanRadio:
                    columnProps.filterDropdown = this.booleanRadioFilter(columnState, column.columnProps.title);
                    columnProps.filterDropdownVisible = columnState.filterVisible;
                    columnProps.onFilterDropdownVisibleChange = this.onFilterVisibleChange(columnState);
                    columnProps.filterIcon = this.filterIcon(columnState);
                    break;
                case Filterer.RadioSelect:
                    columnProps.filterDropdown = this.radioSelectFilter(columnState, column.columnProps.title, column.filterItems);
                    columnProps.filterDropdownVisible = columnState.filterVisible;
                    columnProps.onFilterDropdownVisibleChange = this.onFilterVisibleChange(columnState);
                    columnProps.filterIcon = this.filterIcon(columnState);
                    break;
                default:
                    break;
            }

            switch (column.renderer) {
                case Renderer.BooleanYesNo:
                    columnProps.render = (value: any, record: T) =>
                        this.renderBooleanYesNo(column.renderDataTransform ? column.renderDataTransform(value, record) : value);
                    break;
                case Renderer.BooleanYesEmpty:
                    columnProps.render = (value: any, record: T) =>
                        this.renderBooleanYesEmpty(column.renderDataTransform ? column.renderDataTransform(value, record) : value);
                    break;
                case Renderer.BooleanCheckbox:
                    columnProps.render = (value: any, record: T) =>
                        this.renderBooleanCheckbox(column.renderDataTransform ? column.renderDataTransform(value, record) : value);
                    break;
                case Renderer.ShortDate:
                    columnProps.render = (value: any, record: T) =>
                        this.renderShortDate(column.renderDataTransform ? column.renderDataTransform(value, record) : value);
                    break;
                case Renderer.ShortDateTime:
                    columnProps.render = (value: any, record: T) =>
                        this.renderShortDateTime(column.renderDataTransform ? column.renderDataTransform(value, record) : value);
                    break;
                case Renderer.Currency:
                    columnProps.render = (value: any, record: T) =>
                        this.renderCurrency(column.renderDataTransform ? column.renderDataTransform(value, record) : value);
                    break;

                case Renderer.Number:
                    columnProps.render = (value: any, record: T) =>
                        this.renderNumber(column.renderDataTransform ? column.renderDataTransform(value, record) : value);
                    break;
                case Renderer.NumberAllowZero:
                    columnProps.render = (value: any, record: T) =>
                        this.renderNumberAllowZero(column.renderDataTransform ? column.renderDataTransform(value, record) : value);
                    break;
                case Renderer.LinkButtons:
                    columnProps.render = (value: any, record: T) =>
                        this.renderLinkButtons(column.renderDataTransform ? column.renderDataTransform(value, record) : value, column.toolTipText, column.icon);
                    break;
                default:
                    if (!columnProps.render) {
                        columnProps.render = (value: any, record: T) =>
                            column.renderDataTransform ? column.renderDataTransform(value, record) : value;
                    }
                    break;
            }

            if (columnProps.sorter) {
                if (columnProps.sorter === true && !this.props.serverSide) {
                    columnProps.sorter = (a, b) => {
                        return columnProps.dataIndex ?
                            ([a[columnProps.dataIndex], b[columnProps.dataIndex]].sort()[0] === a[columnProps.dataIndex] ? 1 : -1) :
                            0;
                        // a[columnProps.dataIndex].localeCompare(b[columnProps.dataIndex]);
                    };
                }
                columnProps.sortOrder = this.state.sorter.columnKey === columnProps.dataIndex && this.state.sorter.order;
            }

            return columnProps;
        });

        let { pagination, filters, sorter } = this.state;

        return (
            <div className={this.props.className}>
                {this.props.buttonBar ?
                    <div className="dataTable-button-bar">
                        <ButtonGroup>
                            {this.props.buttonBar.map((button, index) => {
                                switch (button.getType()) {
                                    case DataTableButtonType.Export:
                                        var exportButton = button as DataTableButtonExport;
                                        var filename = `${exportButton.filename}_${moment().format("YYYYMMDDHHmmss")}`;
                                        button.onClick = () => this.exportTable(filename);
                                        break;
                                    case DataTableButtonType.Reset:
                                        button.onClick = () => this.resetTable();
                                        break;
                                    case DataTableButtonType.Link:
                                        // TODO: JB - Please come fix this
                                        return <Button key={button.id} onClick={button.onClick} href={(button as DataTableButtonLink).href}>{button.label}</Button>;
                                    default:
                                        break;
                                }

                                return <Button key={button.id} onClick={() => button.onClick()}>{button.label}</Button>;
                            })}
                        </ButtonGroup>
                    </div>
                    : null}
                <Table {...tableProps} />
            </div>
        );
    }

    private callFetchData(pagination: any, filters: any, sorter: any) {
        if (this.props.fetchData) {
            let requestState =
                this.getTableStateDTO(
                    pagination,
                    filters,
                    sorter
                );

            this.props.fetchData(
                requestState,
                () => requestState.echo === this.echo,
                (response) => {
                    const pager = { ...this.state.pagination };
                    pager.total = response.filteredCount;
                    pager.dataTableUnfilteredTotal = response.totalCount;
                    this.setState({
                        data: response.results || [],
                        pagination: pager
                    });
                }
            );
        }
    }

    private callExportData(pagination: any, filters: any, sorter: any, filename: string) {
        if (this.props.fetchData) {
            let requestState =
                this.getTableStateDTO(
                    pagination,
                    filters,
                    sorter
                );

            // Export specific code
            requestState.runAsExport = true;

            return this.props.fetchData(
                requestState,
                () => requestState.echo === this.echo,
                (response) => {
                    // We don't need to update the table since no new data is coming back

                    let reportName = filename + '.xlsx';
                    downloadFile(response.reportData, reportName);

                    // const pager = { ...this.state.pagination };
                    // pager.total = response.filteredCount;
                    // pager.dataTableUnfilteredTotal = response.totalCount;
                    // this.setState({
                    //     data: response.results || [],
                    //     pagination: pager
                    // });
                }
            );
        }
    }

    private handleTableChange(pagination: any, filters: any, sorter: any) {
        const pager = { ...this.state.pagination };
        pager.current = pagination.current;
        pager.pageSize = pagination.pageSize;

        this.setState({
            pagination: pager,
            sorter: sorter
        });

        this.callFetchData(pager, this.state.filters, sorter);
    }

    private getTableStateDTO(pagination: any, filters: any, sorter: any): TableRequestDTO {
        this.echo += 1;
        return {
            pageLength: pagination.pageSize || pagination.defaultPageSize,
            page: (pagination.current || pagination.defaultCurrent) - 1,
            sortField: sorter.field || null,
            sortOrder: sorter.order || null,
            echo: this.echo,
            filters: filters,
            runAsExport: false
        };
    }

    private textFilter(stateObject: TableColumnState, columnTitle: any): any {
        return (
            <div className="text-filter-dropdown">
                <label className="screen-reader-hidden" htmlFor={`textFilter-${stateObject.columnProp}`}>Text Filter {stateObject.columnProp}</label>
                <Input
                    id={`textFilter-${stateObject.columnProp}`}
                    ref={(ele: any) => this.searchInputs[stateObject.columnProp] = ele}
                    value={stateObject.searchText || ''}
                    onChange={(e: any) => this.onInputChange(e, stateObject)}
                    onPressEnter={() => this.onSearch(stateObject)}
                />
                <Button type="primary" onClick={(e: any) => this.onSearch(stateObject)}>
                    <Translate id='dataTable.search' />
                </Button>
            </div>
        );
    }

    private dateFilter(stateObject: TableColumnState, columnTitle: any): any {
        let div: any;
        let searchButton: any;
        return (
            <div ref={(ele: any) => div = ele} className="date-filter-dropdown">
                <DatePicker
                    ref={(ele: any) => this.searchInputs[stateObject.columnProp] = ele}
                    onChange={(date, dateString) => this.onDateInputChange(date, dateString, stateObject, searchButton)}
                    getCalendarContainer={() => div}
                    format="M/D/YYYY"
                />
                <Button
                    ref={(ele: any) => searchButton = ReactDOM.findDOMNode(ele)}
                    type="primary"
                    onClick={() => this.onSearch(stateObject)}
                >
                    <Translate id='dataTable.search' />
                </Button>
            </div>
        );
    }

    private dateRangeFilter(stateObject: TableColumnState, columnTitle: any): any {
        let div: any;
        let searchButton: any;
        return (
            <div ref={(ele: any) => div = ele} className="daterange-filter-dropdown">
                <RangePicker
                    ref={(ele: any) => this.searchInputs[stateObject.columnProp] = ele}
                    onChange={(date, dateString) => this.onDateRangeInputChange(date, dateString, stateObject, searchButton)}
                    onOpenChange={status => this.onDateRangePickerOpenChange(status, stateObject)}
                    getCalendarContainer={() => div}
                    format="M/D/YYYY"
                />
                <Button
                    ref={(ele: any) => searchButton = ReactDOM.findDOMNode(ele)}
                    type="primary"
                    onClick={() => this.onSearch(stateObject)}
                >
                    Search
                </Button>
            </div>
        );
    }

    private booleanRadioFilter(stateObject: TableColumnState, columnTitle: any): any {
        let searchButton: any;
        return (
            <div className="boolean-radio-filter-dropdown">
                <RadioGroup
                    ref={ele => this.searchInputs[stateObject.columnProp] = ele}
                    onChange={(e) => this.onInputChange(e, stateObject, searchButton)}
                    value={stateObject.searchText ? stateObject.searchText : ''}>
                    <Radio value="true">Yes</Radio>
                    <Radio value="false">No</Radio>
                    <Radio value="">All</Radio>
                </RadioGroup>
                <Button
                    ref={(ele: any) => searchButton = ReactDOM.findDOMNode(ele)}
                    type="primary"
                    onClick={() => this.onSearch(stateObject)}
                >
                    Search
                </Button>
            </div>
        );
    }

    private radioSelectFilter(stateObject: TableColumnState, columnTitle: any, filterItems: string[]): any {
        let searchButton: any;
        // Dev Note: I tried to make an other search box. However, it is much more complicated than I want to go into right now, so here is lays
        return (
            <div className="boolean-radio-filter-dropdown">
                <RadioGroup
                    ref={ele => this.searchInputs[stateObject.columnProp] = ele}
                    onChange={(e) => this.onInputChange(e, stateObject, searchButton)}
                    value={stateObject.searchText ? stateObject.searchText : ''}
                    className="vertical"
                >
                    {filterItems.map(x => (<Radio value={x}>{x}</Radio>))}
                    <Radio value="">All</Radio>
                    {/* <Radio value="___">More
                        {stateObject.searchText === "___" ? <Input
                            id={`textFilter-${stateObject.columnProp}`}
                            ref={(ele: any) => this.searchInputs[stateObject.columnProp] = ele}
                            value={stateObject.searchText || ''}
                            onChange={(e: any) => this.onInputChange(e, stateObject)}
                            onPressEnter={() => this.onSearch(stateObject)}
                        /> : null}
                    </Radio> */}
                </RadioGroup>
                <Button
                    ref={(ele: any) => searchButton = ReactDOM.findDOMNode(ele)}
                    type="primary"
                    onClick={() => this.onSearch(stateObject)}
                >
                    Search
                </Button>
            </div>
        );
    }

    private filterIcon(stateObject: TableColumnState) {
        return <Icon type="filter" style={{ color: stateObject.filtered ? '#108ee9' : '#aaa' }} />;
    }

    private onFilterVisibleChangeWithFocus(stateObject: TableColumnState): any {
        return (visible: boolean) => {
            var stateCopy = { ...stateObject };
            stateCopy.filterVisible = visible;

            this.setState(
                { columnStates: this.getUpdatedColumnStateList(stateCopy) },
                () => this.searchInputs[stateCopy.columnProp] && this.searchInputs[stateCopy.columnProp].focus());
        };
    }

    private onFilterVisibleChange(stateObject: TableColumnState): any {
        return (visible: boolean) => {
            var stateCopy = { ...stateObject };
            stateCopy.filterVisible = visible;

            this.setState({
                columnStates: this.getUpdatedColumnStateList(stateCopy)
            });
        };
    }

    private onDateRangePickerOpenChange(open: boolean, stateObject: TableColumnState): any {
        // return (status: boolean) => {
        // let picker = this.searchInputs[stateObject.columnProp];
        // let currentText = (picker.value[0] as moment.Moment).format(picker.format) +
        //     ' - ' + (picker.value[1] as moment.Moment).format(picker.format);

        console.log(`date range picker ${status ? 'opened' : 'closed'}`);

        // if (currentText !== stateObject.searchText) {
        //     console.log('date range filter changed');
        // }

        // var stateCopy = { ...stateObject };
        // stateCopy.filterVisible = visible;

        // this.setState(
        //     { columnStates: this.getUpdatedColumnStateList(stateCopy) },
        //     () => this.searchInputs[stateCopy.columnProp] && this.searchInputs[stateCopy.columnProp].focus());
        // };
    }

    private onInputChange = (e: any, stateObject: TableColumnState, searchButton?: any) => {
        var stateCopy = { ...stateObject };
        stateCopy.searchText = e.target.value;

        this.setState(
            { columnStates: this.getUpdatedColumnStateList(stateCopy) },
            () => {
                console.log('filter input: state saved');
                if (searchButton) {
                    searchButton.focus();
                }
            });
    }

    private onDateInputChange = (date: Moment, dateString: string, stateObject: TableColumnState, searchButton: any) => {
        var stateCopy = { ...stateObject };
        stateCopy.searchText = dateString;

        this.setState(
            { columnStates: this.getUpdatedColumnStateList(stateCopy) },
            () => {
                console.log('filter input: state saved');
                searchButton.focus();
            });
    }

    private onDateRangeInputChange = (dates: RangePickerValue, dateStrings: [string, string], stateObject: TableColumnState, searchButton: any) => {
        var stateCopy = { ...stateObject };
        stateCopy.searchText = `${dateStrings[0]} - ${dateStrings[1]}`;

        this.setState(
            { columnStates: this.getUpdatedColumnStateList(stateCopy) },
            () => {
                console.log('filter input: state saved');
                searchButton.focus();
            });
    }

    private onSearch = (stateObject: TableColumnState) => {
        var stateCopy = { ...stateObject };
        const { searchText } = stateCopy;
        const reg = new RegExp(searchText ? searchText : '', 'gi');
        stateCopy.filterVisible = false;
        stateCopy.filtered = !!searchText;

        let filters = this.state.filters.map(f => f);
        let filterObj = filters.filter(value => value.columnProp === stateCopy.columnProp);
        if (filterObj.length > 0) {
            filterObj[0].filter = searchText;
        } else {
            filters.push({ columnProp: stateCopy.columnProp, filter: searchText });
        }

        if (this.props.serverSide) {
            this.setState({
                filters,
                pagination: { ...this.state.pagination, current: 1 }, // Set current page to 1 because we just filtered
                columnStates: this.getUpdatedColumnStateList(stateCopy)
            }, () => this.callFetchData(this.state.pagination, filters, this.state.sorter));
        } else {
            this.setState({
                filters,
                columnStates: this.getUpdatedColumnStateList(stateCopy),
                filteredData: this.props.data.map((item: any): any => {
                    const match = item[stateCopy.columnProp].match(reg);
                    if (!match) {
                        return null;
                    }
                    return {
                        ...item,
                    };
                }).filter((item: any) => !!item)
            });
        }
    }

    private getColumnState(customColumnProps: DataTableColumnProps<T>): TableColumnState {
        return this.state.columnStates.filter(
            cs => cs.columnProp ===
                customColumnProps.columnProps.dataIndex || customColumnProps.columnProps.key
        )[0];
    }

    private getUpdatedColumnStateList(modifiedColumnState: TableColumnState): TableColumnState[] {
        return this.state.columnStates.map(
            cs => cs.columnProp === modifiedColumnState.columnProp ? modifiedColumnState : cs);
    }

    private renderShortDate(value: string) {
        if (!value) {
            return;
        }

        let date = moment.utc(value);
        return (
            <span>{date.format('M/D/YYYY')}</span>
        );
    }

    private renderShortDateTime(value: string) {
        if (!value) {
            return;
        }

        let date = moment.utc(value);
        date.local();
        return (
            <span>{date.format('M/D/YYYY hh:mm:ss A')}</span>
        );
    }

    private renderCurrency(value: string) {
        if (!value) {
            return;
        }

        try {
            let num = parseFloat(value);
            return (
                <span>${num.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,')}</span>
            );
        } catch (error) {
            return;
        }
    }

    private renderNumber(value: string) {
        if (!value) {
            return;
        }

        try {
            return (
                <span>{`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</span>
            );
        } catch (error) {
            return;
        }
    }

    private renderNumberAllowZero(value: string) {
        try {
            return (
                <span>{`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</span>
            );
        } catch (error) {
            return;
        }
    }

    private renderBooleanYesNo(value: boolean) {
        return value ? 'Yes' : 'No';
    }

    private renderBooleanYesEmpty(value: boolean) {
        return value ? 'Yes' : '';
    }

    private renderBooleanCheckbox(value: boolean) {
        return <Checkbox defaultChecked={value} disabled={true} />;
    }

    private renderLinkButtons(links: string | string[], toolTipText: JSX.Element, icon: string) {
        if (!links || links.length === 0) {
            return (
                <span>
                    {this.renderDisabledLinkButton(toolTipText, icon)}
                </span>
            );
        }

        let isString = typeof links === 'string';
        let strVal = links as string;
        let arrVal = links as string[];
        return (
            <span>
                {isString ?
                    this.renderLinkButton(strVal, toolTipText, icon) :
                    arrVal.map(link => this.renderLinkButton(link, toolTipText, icon))}
            </span>
        );
    }

    private renderLinkButton(link: string, toolTipText: JSX.Element, icon: string) {
        return (
            <Tooltip placement="bottom" title={toolTipText} arrowPointAtCenter>
                <Link to={link}>
                    <Button shape="circle" icon={icon} />
                </Link>
            </Tooltip>
        );
    }

    private renderDisabledLinkButton(toolTipText: JSX.Element, icon: string) {
        return (
            <Tooltip placement="bottom" title={toolTipText} arrowPointAtCenter>
                <Link to="">
                    <Button shape="circle" icon={icon} disabled />
                </Link>
            </Tooltip>
        );
    }
}

export default DataTable;



// WEBPACK FOOTER //
// ./src/components/common/DataTable.tsx